import { Lock, ShieldOff } from 'lucide-react';
import { ButtonLink } from '@/components/ui/Button';

/**
 * The UNAUTHORIZED and FORBIDDEN states from BUILD-PLAN §55, kept distinct
 * because they call for different things from the customer:
 *
 *   Unauthorized — "we don't know who you are": offer a way to sign in.
 *   Forbidden    — "we know, and you may not": offering sign-in would be
 *                  misleading, so it does not.
 */

interface AccessStateProps {
  /** Where to return after signing in. */
  returnTo?: string;
}

export function UnauthorizedState({ returnTo }: AccessStateProps) {
  const to = returnTo ? `/login?returnTo=${encodeURIComponent(returnTo)}` : '/login';

  return (
    <section className="flex min-h-[60vh] flex-col items-center justify-center px-6 py-16 text-center">
      <span
        className="mb-6 flex h-14 w-14 items-center justify-center rounded-full bg-surface-muted"
        aria-hidden="true"
      >
        <Lock className="h-6 w-6 text-accent" strokeWidth={1.5} />
      </span>

      <h1 className="font-display text-2xl text-ink">
        Please sign in to continue
      </h1>

      <p className="mt-3 max-w-sm text-sm leading-relaxed text-muted">
        This part of Glanz Luxe is for account holders. Sign in, or create an account in
        a moment.
      </p>

      <div className="mt-8 flex flex-col gap-3 sm:flex-row">
        <ButtonLink to={to}>Sign In</ButtonLink>
        <ButtonLink to="/register" variant="secondary">
          Create an Account
        </ButtonLink>
      </div>
    </section>
  );
}

export function ForbiddenState() {
  return (
    <section className="flex min-h-[60vh] flex-col items-center justify-center px-6 py-16 text-center">
      <span
        className="mb-6 flex h-14 w-14 items-center justify-center rounded-full bg-surface-muted"
        aria-hidden="true"
      >
        <ShieldOff className="h-6 w-6 text-accent" strokeWidth={1.5} />
      </span>

      <h1 className="font-display text-2xl text-ink">
        You don’t have access to this
      </h1>

      <p className="mt-3 max-w-sm text-sm leading-relaxed text-muted">
        Your account doesn’t have permission to view this page. If you think that’s a
        mistake, please contact us.
      </p>

      <div className="mt-8">
        <ButtonLink to="/" variant="secondary">
          Back to Home
        </ButtonLink>
      </div>
    </section>
  );
}
